import type {ImageFormat} from '../constants/converterFormats'
import {DEFAULT_JPEG_QUALITY} from '../constants/converterFormats'
import {encodeBmp} from './bmpEncoder'
import {encodeIco} from './icoEncoder'
import {encodeStaticGif} from './gifEncoder'

export interface ConvertImageOptions {
  /** JPEG/WebP quality, 0..1 */
  quality?: number
  /** ICO only — sizes to embed in the multi-resolution .ico */
  icoSizes?: readonly number[]
}

// JPEG and BMP have no alpha channel — transparent source pixels must be
// flattened onto a solid background first or they render black/undefined.
const FORMATS_WITHOUT_ALPHA = new Set<ImageFormat>(['jpeg', 'bmp'])

const drawToCanvas = (image: HTMLImageElement, flattenToWhite: boolean): HTMLCanvasElement => {
  const canvas = document.createElement('canvas')
  canvas.width = image.naturalWidth || image.width
  canvas.height = image.naturalHeight || image.height
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas 2D context unavailable')
  if (flattenToWhite) {
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
  }
  ctx.drawImage(image, 0, 0)
  return canvas
}

const canvasToBlob = (canvas: HTMLCanvasElement, mimeType: string, quality?: number): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob)
        else reject(new Error('canvas.toBlob returned null'))
      },
      mimeType,
      quality,
    )
  })

/** Single dispatch point for every image-converter output format. */
export const convertImage = async (
  image: HTMLImageElement,
  targetFormat: ImageFormat,
  options: ConvertImageOptions = {},
): Promise<Blob> => {
  if (targetFormat === 'ico') {
    const sizes = options.icoSizes && options.icoSizes.length > 0 ? options.icoSizes : [256]
    return encodeIco(image, sizes)
  }

  const canvas = drawToCanvas(image, FORMATS_WITHOUT_ALPHA.has(targetFormat))

  switch (targetFormat) {
    case 'png':
      return canvasToBlob(canvas, 'image/png')
    case 'jpeg':
      return canvasToBlob(canvas, 'image/jpeg', options.quality ?? DEFAULT_JPEG_QUALITY)
    case 'webp':
      return canvasToBlob(canvas, 'image/webp', options.quality ?? DEFAULT_JPEG_QUALITY)
    case 'bmp':
      return encodeBmp(canvas)
    case 'gif':
      return encodeStaticGif(canvas)
    default:
      throw new Error(`Unsupported target format: ${targetFormat as string}`)
  }
}
